import React, { useEffect, useMemo, useState } from 'react'
import { X, Loader2, BarChart3, Inbox, ChevronDown, ChevronRight, Users, GraduationCap, Check, Minus, Sparkles, Lightbulb, Film } from 'lucide-react'
import { resultApi } from '../api/lessonBuilderApi'

interface Props {
  lessonId: string
  lessonTitle?: string
  onClose: () => void
}

// Giờ VIỆT NAM: created_at do backend trả là UTC (có 'Z'); bản ghi cũ thiếu tz thì coi như UTC.
const fmtVN = (iso?: string) => {
  if (!iso) return ''
  try {
    const s = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z'
    return new Date(s).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false })
  } catch {
    return ''
  }
}
const pct10 = (r: any) => (r?.max_score ? (r.score / r.max_score) * 10 : 0)

type StudentGroup = { key: string; name?: string; email?: string; class_name: string; attempts: any[]; student_id?: string }

// Bảng breakdown TỪNG PHẦN của 1 lượt làm (detail.sections do LessonViewerPage gửi).
const SectionBreakdown: React.FC<{ detail: any }> = ({ detail }) => {
  const sections: any[] = Array.isArray(detail?.sections) ? detail.sections : []

  return (
    <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3 my-1">
      {/* Tóm tắt tương tác */}
      <div className="flex flex-wrap gap-2 mb-2 text-[11px] font-semibold">
        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Đúng {detail?.correct ?? 0}</span>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">✗ Sai {detail?.wrong ?? 0}</span>
        <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 inline-flex items-center gap-1">
          <Lightbulb size={11} /> Gợi ý {detail?.hintsUsed ?? 0}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 inline-flex items-center gap-1">
          <Sparkles size={11} /> Hỏi AI {detail?.aiAsked ?? 0}
        </span>
        {detail?.videoWatched !== undefined && (
          <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 inline-flex items-center gap-1">
            <Film size={11} /> Video {detail.videoWatched}
          </span>
        )}
        {detail?.totalSections !== undefined && (
          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            Đã học {detail?.sectionsDone ?? detail?.sectionsViewed ?? 0}/{detail.totalSections} phần · {detail?.contentPercent ?? 0}%
          </span>
        )}
      </div>

      {sections.length === 0 ? (
        <p className="text-[11px] text-gray-400">Lượt làm này chưa có dữ liệu chi tiết theo từng phần (bản ghi cũ).</p>
      ) : (
        <table className="w-full text-xs bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-100 bg-gray-50/60">
              <th className="py-1.5 px-2">Phần</th>
              <th className="py-1.5 px-2 text-center">Đã xem</th>
              <th className="py-1.5 px-2 text-center">Câu hỏi</th>
              <th className="py-1.5 px-2 text-center text-green-600">Đúng</th>
              <th className="py-1.5 px-2 text-center text-red-500">Sai</th>
              <th className="py-1.5 px-2 text-center text-yellow-600">Gợi ý</th>
              <th className="py-1.5 px-2 text-center text-purple-600">Hỏi AI</th>
              <th className="py-1.5 px-2 text-center">Đã học</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sections.map((s) => {
              const answered = (s.correct || 0) + (s.wrong || 0)
              const pending = (s.totalQuestions || 0) - answered
              return (
                <tr key={s.idx} className={s.done ? '' : 'bg-amber-50/40'}>
                  <td className="py-1.5 px-2 font-medium text-gray-700">{s.idx + 1}. {s.title}</td>
                  <td className="py-1.5 px-2 text-center">
                    {s.viewed ? <Check size={13} className="text-green-600 inline" /> : <Minus size={13} className="text-gray-300 inline" />}
                  </td>
                  <td className="py-1.5 px-2 text-center text-gray-500">{s.totalQuestions}</td>
                  <td className="py-1.5 px-2 text-center font-semibold text-green-700">{s.correct}</td>
                  <td className="py-1.5 px-2 text-center font-semibold text-red-600">{s.wrong}</td>
                  <td className="py-1.5 px-2 text-center text-yellow-700">{s.hints}</td>
                  <td className="py-1.5 px-2 text-center text-purple-700">{s.ai}</td>
                  <td className="py-1.5 px-2 text-center">
                    {s.done
                      ? <Check size={13} className="text-green-600 inline" />
                      : <span className="text-[10px] text-amber-600 font-semibold">
                          {!s.viewed ? 'chưa xem' : pending > 0 ? `còn ${pending} câu` : 'chưa xong'}
                        </span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// Một LƯỢT làm (Lần k) — bấm để mở chi tiết từng phần.
const AttemptRow: React.FC<{ r: any; label: string }> = ({ r, label }) => {
  const hasDetail = r.detail && typeof r.detail === 'object' && !Array.isArray(r.detail)
  const [open, setOpen] = useState(false)
  return (
    <div>
      <div
        onClick={() => hasDetail && setOpen((o) => !o)}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${hasDetail ? 'cursor-pointer hover:bg-blue-50/50' : ''}`}
      >
        {hasDetail
          ? (open ? <ChevronDown size={13} className="text-blue-500 shrink-0" /> : <ChevronRight size={13} className="text-gray-300 shrink-0" />)
          : <span className="w-[13px] shrink-0" />}
        <span className="text-xs font-bold text-gray-500 w-16 shrink-0">{label}</span>
        <span className="font-bold text-blue-700 w-16 shrink-0">{r.score}/{r.max_score}</span>
        <span className="text-gray-500 text-xs w-14 shrink-0">{r.correct}/{r.total}</span>
        <span className="text-gray-400 text-xs ml-auto text-right">{fmtVN(r.created_at)}</span>
      </div>
      {open && hasDetail && <div className="pl-6"><SectionBreakdown detail={r.detail} /></div>}
    </div>
  )
}

// Câu trả lời PHIẾU HỌC TẬP của 1 HS (tự luận ngắn + tự đánh giá Đạt/Chưa).
const WorksheetBlock: React.FC<{ ws: any[] }> = ({ ws }) => {
  const items = ws.filter((a) => (a.answer || '').trim() || a.self_assess)
  if (items.length === 0) return null
  return (
    <div className="mt-1.5 border-t border-gray-100 pt-1.5">
      <div className="text-[11px] font-bold text-teal-700 mb-1">📋 Phiếu học tập</div>
      <div className="space-y-1">
        {items.map((a, i) => (
          <div key={i} className="text-xs">
            {a.self_assess
              ? <span className={a.self_assess === 'dat' ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>Tự đánh giá: {a.self_assess === 'dat' ? 'Đạt' : 'Chưa đạt'}</span>
              : <span className="text-gray-700"><b className="text-gray-400">Trả lời:</b> {a.answer}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

// Khối 1 học sinh: tên/email (+lớp) + các lượt Lần 1,2,3… + phiếu học tập (nếu có).
const StudentBlock: React.FC<{ g: StudentGroup; showClass?: boolean; ws?: any[] }> = ({ g, showClass, ws }) => (
  <div className="border border-gray-100 rounded-xl p-2.5 mb-2">
    <div className="flex items-center gap-2 px-1 mb-1">
      <div className="min-w-0">
        <div className="font-semibold text-gray-800 truncate">{g.name || '—'}</div>
        <div className="text-[11px] text-gray-400 truncate">
          {g.email}{showClass && g.class_name ? <> · <span className="font-semibold text-gray-500">{g.class_name}</span></> : ''}
        </div>
      </div>
      <span className="ml-auto text-[11px] font-bold text-gray-400 shrink-0">{g.attempts.length} lượt</span>
    </div>
    <div className="space-y-0.5">
      {g.attempts.map((r, i) => <AttemptRow key={r.id} r={r} label={`Lần ${i + 1}`} />)}
    </div>
    {ws && ws.length > 0 && <WorksheetBlock ws={ws} />}
  </div>
)

const ResultsModal: React.FC<Props> = ({ lessonId, lessonTitle, onClose }) => {
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState<'student' | 'class'>('student')
  const [wsStudents, setWsStudents] = useState<any[]>([])   // {student_id,name,email,answers}[]

  useEffect(() => {
    resultApi.list(lessonId).then((r) => {
      setRows(r)
      setLoading(false)
    })
    resultApi.worksheetAll(lessonId).then((d) => setWsStudents(d.students || [])).catch(() => {})
  }, [lessonId])

  const wsById = useMemo(() => {
    const m: Record<string, any> = {}
    for (const s of wsStudents) m[s.student_id] = s
    return m
  }, [wsStudents])

  // Gom theo TÀI KHOẢN; mỗi tài khoản nhiều lượt → đánh Lần 1..N (theo thời gian tăng dần).
  const students = useMemo<StudentGroup[]>(() => {
    const m = new Map<string, StudentGroup>()
    const sids = new Set<string>()
    for (const r of rows) {
      const key = String(r.student_email || r.student_id || r.student_name || r.id)
      let g = m.get(key)
      if (!g) { g = { key, name: r.student_name, email: r.student_email, class_name: (r.class_name || '').trim(), attempts: [], student_id: r.student_id }; m.set(key, g) }
      g.attempts.push(r)
      if (r.student_id) sids.add(r.student_id)
    }
    // HS CHỈ trả lời phiếu (chưa phát sinh EbLessonResult) → thêm vào để GV vẫn thấy phiếu.
    for (const s of wsStudents) {
      if (s.student_id && !sids.has(s.student_id)) {
        const key = String(s.email || s.student_id)
        if (!m.has(key)) m.set(key, { key, name: s.name, email: s.email, class_name: '', attempts: [], student_id: s.student_id })
      }
    }
    const arr = Array.from(m.values())
    for (const g of arr) g.attempts.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')))
    // nhóm sắp theo lượt GẦN NHẤT giảm dần
    arr.sort((a, b) => String(b.attempts[b.attempts.length - 1]?.created_at || '').localeCompare(String(a.attempts[a.attempts.length - 1]?.created_at || '')))
    return arr
  }, [rows, wsStudents])

  const hasClasses = useMemo(() => students.some((g) => g.class_name), [students])

  // Gom theo LỚP (mỗi lớp: sĩ số làm bài, số lượt, điểm TB quy đổi 10).
  const classes = useMemo(() => {
    const m = new Map<string, StudentGroup[]>()
    for (const g of students) {
      const cn = g.class_name || '(Chưa phân lớp)'
      if (!m.has(cn)) m.set(cn, [])
      m.get(cn)!.push(g)
    }
    return Array.from(m.entries()).map(([cn, gs]) => {
      const attempts = gs.flatMap((g) => g.attempts)
      const avg = attempts.length ? attempts.reduce((s, r) => s + pct10(r), 0) / attempts.length : 0
      return { cn, gs, students: gs.length, attemptCount: attempts.length, avg }
    }).sort((a, b) => a.cn.localeCompare(b.cn, 'vi'))
  }, [students])

  const avg = rows.length > 0 ? rows.reduce((s, r) => s + pct10(r), 0) / rows.length : 0

  const Toggle = () => (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs font-bold">
      <button onClick={() => setMode('student')}
        className={`inline-flex items-center gap-1 px-2.5 py-1 ${mode === 'student' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
        <Users size={13} /> Theo học sinh
      </button>
      <button onClick={() => setMode('class')}
        className={`inline-flex items-center gap-1 px-2.5 py-1 border-l border-gray-200 ${mode === 'class' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
        <GraduationCap size={13} /> Theo lớp
      </button>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={20} />
            <h2 className="text-lg font-black text-gray-900">Kết quả học tập</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
        </div>

        <div className="px-4 py-2 border-b border-gray-50 flex items-center gap-2 flex-wrap">
          <div className="text-sm text-gray-500 min-w-0">
            {lessonTitle && <span className="font-semibold text-gray-700">{lessonTitle}</span>}
            {rows.length > 0 && <span> · {students.length} học viên · {rows.length} lượt làm · TB quy đổi 10: <strong className="text-blue-700">{avg.toFixed(1)}</strong></span>}
          </div>
          {rows.length > 0 && hasClasses && <div className="ml-auto"><Toggle /></div>}
          {rows.length > 0 && (
            <div className="w-full text-[11px] text-gray-400">— bấm vào từng lượt để xem chi tiết theo phần</div>
          )}
        </div>

        <div className="overflow-y-auto p-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-gray-300">
              <Inbox size={40} className="mb-2" />
              <p className="text-gray-400 text-sm">Chưa có lượt làm bài nào.</p>
            </div>
          ) : mode === 'class' && hasClasses ? (
            classes.map((c) => (
              <div key={c.cn} className="mb-4">
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1">
                  <GraduationCap size={16} className="text-blue-600" />
                  <span className="font-black text-gray-800">{c.cn}</span>
                  <span className="text-[11px] font-bold text-gray-400">· {c.students} HS · {c.attemptCount} lượt · TB {c.avg.toFixed(1)}</span>
                </div>
                {c.gs.map((g) => <StudentBlock key={g.key} g={g} ws={wsById[g.student_id || g.attempts[0]?.student_id]?.answers} />)}
              </div>
            ))
          ) : (
            students.map((g) => <StudentBlock key={g.key} g={g} showClass={hasClasses} ws={wsById[g.student_id || g.attempts[0]?.student_id]?.answers} />)
          )}
        </div>
      </div>
    </div>
  )
}

export default ResultsModal
